import { buyerPhoneSchema } from '@massilia/contracts'
import { firstNameSchema, lastNameSchema } from '@massilia/features'
import { validatePhoneNumber } from '@massilia/queries'
import { z } from 'zod'

// Paramètres du lien /payer-acompte (contrat, SMS, mails).
export const depositSearchSchema = z.object({
  email: z.coerce.string().optional(),
  lastName: z.coerce.string().optional(),
  // Le routeur lit `?dossierNumber=12345678` comme un nombre : une référence fournisseur
  // toute en chiffres (dossier sans PNR) faisait planter la page. Au-delà de 15 chiffres,
  // le nombre perd sa précision et la recherche échoue proprement (dossier introuvable).
  dossierNumber: z.coerce.string().optional(),
  // Moyen de règlement visé : le PDF de contrat et l'espace client pointent vers
  // `...&mode=installments` pour ouvrir directement le paiement en 3/4x.
  mode: z.enum(['installments']).optional(),
})

// Lookup schema: matches against EXISTING stored bookings — do not apply the
// strict name rules, otherwise legacy names with accents couldn't be found.
export const depositLookupSchema = z.object({
  email: z.string().min(1, 'Ce champ est requis').email('Veuillez saisir une adresse email valide'),
  lastName: z.string().min(1, 'Ce champ est requis'),
  dossierNumber: z.string().min(1, 'Ce champ est requis'),
})

/**
 * Règlement du solde d'un dossier existant en 3/4x, depuis /payer-acompte.
 *
 * Reprend mot pour mot les règles de facturation du tunnel
 * (`checkoutFormSchema`, branche `paymentMethod === 'installments'`) : mêmes champs,
 * mêmes messages, même refus des rues purement numériques que SSP rejette.
 * Pas de champ montant : le solde restant est imposé par le serveur.
 */
export const installmentPaymentSchema = z.object({
  billingCity: z.string().trim().min(1, 'La ville est requise'),
  billingCountryCode: z
    .object({
      label: z.string(),
      value: z.string(),
    })
    .refine((country) => country.value.length > 0, { message: 'Le pays est requis' }),
  billingStreet: z
    .string()
    .trim()
    .min(1, "L'adresse est requise")
    // SSP exige un libellé de rue non numérique : on le refuse avant l'appel serveur.
    .refine((street) => /[A-Za-zÀ-ÿ]/.test(street), {
      message: "Merci d'indiquer la rue (ex : 25 Rue de Paris)",
    }),
  billingZipCode: z.string().trim().min(1, 'Le code postal est requis'),
  floaPayType: z.enum(['3XG', '4XG']).optional(),
  phone: buyerPhoneSchema,
})

export const legacyDepositSchema = z.object({
  email: z.string().min(1, 'Ce champ est requis').email('Veuillez saisir une adresse email valide'),
  firstName: firstNameSchema,
  lastName: lastNameSchema,
  amountEuros: z.number().min(0.01, 'Veuillez saisir un montant valide'),
  comment: z.string().optional(),
  dossierNumber: z.string().min(1, 'Ce champ est requis'),
  phone: z
    .string()
    .min(1, 'Ce champ est requis')
    .refine((value) => validatePhoneNumber(value) === undefined, {
      message: 'Numéro de téléphone invalide',
    }),
})
